import type {
  AgingRow,
  AllocateInput,
  ConvertQuotationInput,
  CreditInFullInput,
  CreditInFullPlan,
  CreditInFullResult,
  CreditNote,
  CreditNoteInput,
  CreditNoteListItem,
  CreditNoteSummary,
  CreditNoteUpdate,
  CustomerStatement,
  InvoiceInput,
  InvoiceMessage,
  InvoiceStockShortage,
  InvoiceSummary,
  InvoiceUpdate,
  Page,
  PublicDocumentView,
  PublicLinkCreate,
  PublicLinkIssued,
  PublicLinkStatus,
  Quotation,
  QuotationInput,
  QuotationListItem,
  QuotationSummary,
  QuotationUpdate,
  Receipt,
  ReceiptInput,
  ReceiptSummary,
  SalesInvoice,
  SalesInvoiceListItem,
  SalesSettings,
  SalesSettingsUpdate,
  SendInvoiceEmail,
  SendInvoiceEmailResult,
  TreasuryAccountOption,
} from '@sahab/contracts'
import {
  keepPreviousData,
  type QueryClient,
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { api, type DownloadedFile } from '../../lib/api'
import type { Language } from '../../lib/format'
import { invalidateLedger } from '../accounting/queries'
import { inventoryKeys, warehousesQueryOptions } from '../inventory/queries'
import type {
  CreditNoteListParams,
  CreditNoteSummaryParams,
  InvoiceListParams,
  InvoiceSummaryParams,
  QuotationListParams,
  QuotationSummaryParams,
  ReceiptListParams,
  ReceiptSummaryParams,
} from './search'

/** Every sales query lives under ['sales']: any posting refreshes all of them at once. */
export const salesKeys = {
  all: ['sales'] as const,
  settings: ['sales', 'settings'] as const,
  invoices: ['sales', 'invoices'] as const,
  invoiceList: (query: InvoiceListParams) => [...salesKeys.invoices, 'list', query] as const,
  invoice: (id: string) => [...salesKeys.invoices, 'detail', id] as const,
  invoiceSummary: (query: InvoiceSummaryParams) => [...salesKeys.invoices, 'summary', query] as const,
  stockCheck: (id: string) => [...salesKeys.invoices, 'stock-check', id] as const,
  creditInFull: (id: string) => [...salesKeys.invoices, 'credit-in-full', id] as const,
  invoiceMessages: (id: string) => [...salesKeys.invoices, 'messages', id] as const,
  publicLink: (id: string, entity: ShareEntity = 'invoice') => ['sales', 'public-link', entity, id] as const,
  openInvoices: (customerId: string) => [...salesKeys.invoices, 'open', customerId] as const,
  creditable: (customerId: string) => [...salesKeys.invoices, 'creditable', customerId] as const,
  quotations: ['sales', 'quotations'] as const,
  quotationList: (query: QuotationListParams) => [...salesKeys.quotations, 'list', query] as const,
  quotation: (id: string) => [...salesKeys.quotations, 'detail', id] as const,
  quotationSummary: (query: QuotationSummaryParams) => [...salesKeys.quotations, 'summary', query] as const,
  receipts: ['sales', 'receipts'] as const,
  receiptList: (query: ReceiptListParams) => [...salesKeys.receipts, 'list', query] as const,
  receipt: (id: string) => [...salesKeys.receipts, 'detail', id] as const,
  receiptSummary: (query: ReceiptSummaryParams) => [...salesKeys.receipts, 'summary', query] as const,
  creditNotes: ['sales', 'credit-notes'] as const,
  creditNoteList: (query: CreditNoteListParams) => [...salesKeys.creditNotes, 'list', query] as const,
  creditNote: (id: string) => [...salesKeys.creditNotes, 'detail', id] as const,
  creditNoteSummary: (query: CreditNoteSummaryParams) =>
    [...salesKeys.creditNotes, 'summary', query] as const,
  statement: (customerId: string, from: string, to: string) =>
    ['sales', 'statement', customerId, from, to] as const,
  aging: (asOf: string) => ['sales', 'aging', asOf] as const,
}

/** The largest page the API serves: enough open invoices for one customer's allocation. */
export const OPEN_INVOICES_LIMIT = 200

const path = (base: string, id: string, action?: string) =>
  `${base}/${encodeURIComponent(id)}${action ? `/${action}` : ''}`

// ── reference data ───────────────────────────────────────────────────────────

export const salesSettingsQueryOptions = queryOptions({
  queryKey: salesKeys.settings,
  queryFn: ({ signal }) => api.get<SalesSettings>('/sales/settings', { signal }),
  staleTime: 5 * 60_000,
})

/** Active cash/bank accounts for money-recording forms (no balances; needs no treasury.read). */
export const treasuryOptionsQueryOptions = queryOptions({
  queryKey: ['treasury-accounts', 'options'] as const,
  queryFn: ({ signal }) => api.get<TreasuryAccountOption[]>('/treasury-accounts/options', { signal }),
  staleTime: 5 * 60_000,
})

/** Warehouses (inventory.read) for the invoice and return pickers: the inventory feature's query. */
export { warehousesQueryOptions }

// ── invoices ─────────────────────────────────────────────────────────────────

/** The list's filters, view and sort as query parameters (list and export). */
function invoiceFilters(query: Omit<InvoiceListParams, 'page' | 'pageSize'>) {
  return {
    sort: query.sort,
    q: query.q,
    kind: query.kind,
    status: query.status,
    open: query.open,
    paymentStatus: query.paymentStatus,
    customerId: query.customerId,
    projectId: query.projectId,
    from: query.from,
    to: query.to,
    createdBy: query.createdBy,
    salespersonId: query.salespersonId,
  }
}

export function invoiceListQueryOptions(query: InvoiceListParams) {
  return queryOptions({
    queryKey: salesKeys.invoiceList(query),
    queryFn: ({ signal }) =>
      api.get<Page<SalesInvoiceListItem>>('/sales/invoices', {
        signal,
        query: { page: query.page, pageSize: query.pageSize, ...invoiceFilters(query) },
      }),
    placeholderData: keepPreviousData,
  })
}

/** Counts per view tab (and the open and overdue balances) for the list's other filters. */
export function invoiceSummaryQueryOptions(query: InvoiceSummaryParams) {
  return queryOptions({
    queryKey: salesKeys.invoiceSummary(query),
    queryFn: ({ signal }) =>
      api.get<InvoiceSummary>('/sales/invoices/summary', {
        signal,
        query: {
          q: query.q,
          kind: query.kind,
          customerId: query.customerId,
          projectId: query.projectId,
          from: query.from,
          to: query.to,
          createdBy: query.createdBy,
          salespersonId: query.salespersonId,
        },
      }),
    placeholderData: keepPreviousData,
  })
}

/**
 * `GET /sales/invoices/export` (sales.export): every invoice of the list's view and filters, in its
 * sort, as a CSV file with headers and statuses in the UI language.
 */
export function exportInvoices(query: InvoiceListParams, language: Language): Promise<DownloadedFile> {
  return api.download('/sales/invoices/export', {
    query: { format: 'csv', lang: language, ...invoiceFilters(query) },
    fallbackName: 'invoices.csv',
  })
}

/**
 * A draft's goods lines that exceed what is on hand at its warehouse (invoices.post). Only a
 * warning: sales may take stock below zero (ASM-012).
 */
export function invoiceStockCheckQueryOptions(id: string) {
  return queryOptions({
    queryKey: salesKeys.stockCheck(id),
    queryFn: ({ signal }) =>
      api.get<InvoiceStockShortage[]>(path('/sales/invoices', id, 'stock-check'), { signal }),
    staleTime: 0,
  })
}

export function invoiceQueryOptions(id: string) {
  return queryOptions({
    queryKey: salesKeys.invoice(id),
    queryFn: ({ signal }) => api.get<SalesInvoice>(path('/sales/invoices', id), { signal }),
  })
}

/** A customer's posted invoices and debit notes with an open balance, oldest first. */
export function openInvoicesQueryOptions(customerId: string) {
  return queryOptions({
    queryKey: salesKeys.openInvoices(customerId),
    queryFn: ({ signal }) =>
      api.get<Page<SalesInvoiceListItem>>('/sales/invoices', {
        signal,
        query: { open: true, customerId, pageSize: OPEN_INVOICES_LIMIT, sort: 'invoiceDate' },
      }),
  })
}

/** A customer's posted documents a credit note can be issued against, newest first. */
export function creditableInvoicesQueryOptions(customerId: string) {
  return queryOptions({
    queryKey: salesKeys.creditable(customerId),
    queryFn: ({ signal }) =>
      api.get<Page<SalesInvoiceListItem>>('/sales/invoices', {
        signal,
        query: { status: 'POSTED', customerId, pageSize: 100, sort: '-invoiceDate' },
      }),
  })
}

/** Anything a sales document does can change lists, balances, statements and aging: refresh them all. */
export function invalidateSales(queryClient: QueryClient, options: { ledger?: boolean } = {}) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: salesKeys.all }),
    options.ledger ? invalidateLedger(queryClient) : undefined,
    // Posting and voiding move stock.
    options.ledger ? queryClient.invalidateQueries({ queryKey: inventoryKeys.stock }) : undefined,
    options.ledger ? queryClient.invalidateQueries({ queryKey: inventoryKeys.movements }) : undefined,
  ])
}

function useDocumentMutation<TInput, TResult extends { id: string }>(
  request: (input: TInput) => Promise<TResult>,
  detailKey: (id: string) => readonly unknown[],
  options: { ledger?: boolean } = {},
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: request,
    onSuccess: (doc) => {
      queryClient.setQueryData(detailKey(doc.id), doc)
      return invalidateSales(queryClient, options)
    },
  })
}

export function useSaveInvoice(id: string | null) {
  return useDocumentMutation(
    (input: InvoiceInput | InvoiceUpdate) =>
      id
        ? api.put<SalesInvoice>(path('/sales/invoices', id), input)
        : api.post<SalesInvoice>('/sales/invoices', input),
    salesKeys.invoice,
  )
}

export function usePostInvoice(id: string) {
  return useDocumentMutation(
    (version: number) => api.post<SalesInvoice>(path('/sales/invoices', id, 'post'), { version }),
    salesKeys.invoice,
    { ledger: true },
  )
}

export function useVoidInvoice(id: string) {
  return useDocumentMutation(
    (reason: string) => api.post<SalesInvoice>(path('/sales/invoices', id, 'void'), { reason }),
    salesKeys.invoice,
    { ledger: true },
  )
}

/** What "Credit in full" would post today (G24), read fresh each time the dialog opens. */
export function creditInFullQueryOptions(id: string) {
  return queryOptions({
    queryKey: salesKeys.creditInFull(id),
    queryFn: ({ signal }) =>
      api.get<CreditInFullPlan>(path('/sales/invoices', id, 'credit-in-full'), { signal }),
    staleTime: 0,
    retry: false,
  })
}

/** Credit in full (G24): a credit note dated today for everything not yet credited, and the refund if asked. */
export function useCreditInFull(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreditInFullInput) =>
      api.post<CreditInFullResult>(path('/sales/invoices', id, 'credit-in-full'), input),
    onSuccess: () => invalidateSales(queryClient, { ledger: true }),
  })
}

/**
 * Takes a settlement off an invoice: the receipt's or credit note's allocation is removed and its
 * amount returns to the customer's unapplied credit (receipts.create / creditNotes.create).
 */
export function useRemoveInvoiceAllocation(invoiceId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (allocation: { id: string; sourceType: 'RECEIPT' | 'CREDIT_NOTE'; sourceId: string }) => {
      const source = allocation.sourceType === 'RECEIPT' ? '/sales/receipts' : '/sales/credit-notes'
      return api.delete<unknown>(
        `${path(source, allocation.sourceId, 'allocations')}/${encodeURIComponent(allocation.id)}`,
      )
    },
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: salesKeys.invoice(invoiceId) }),
        invalidateSales(queryClient),
      ]),
  })
}

/** One source of the customer's credit applied to this invoice. */
export interface CreditApplication {
  sourceType: 'RECEIPT' | 'CREDIT_NOTE'
  sourceId: string
  amount: string
}

/**
 * "Apply credit" on an invoice: each receipt or credit note with credit left is allocated to it
 * (`POST …/allocations`), one source after the other. Rejects with the first failure; the ones
 * before it stay applied (the invoice is refreshed either way).
 */
export function useApplyCredit(invoiceId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (applications: readonly CreditApplication[]) => {
      for (const a of applications) {
        const source = a.sourceType === 'RECEIPT' ? '/sales/receipts' : '/sales/credit-notes'
        await api.post<unknown>(path(source, a.sourceId, 'allocations'), {
          allocations: [{ invoiceId, amount: a.amount }],
        })
      }
    },
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: salesKeys.invoice(invoiceId) }),
        invalidateSales(queryClient),
      ]),
  })
}

// ── share links ──────────────────────────────────────────────────────────────

/** The documents that can be shared by link (sales-documents Q11): invoices, then quotations. */
export type ShareEntity = 'invoice' | 'quotation'

const SHARE_BASE: Record<ShareEntity, string> = {
  invoice: '/sales/invoices',
  quotation: '/sales/quotations',
}

/** The document's latest share link (never its token: it is shown once, when created). */
export function publicLinkQueryOptions(id: string, entity: ShareEntity = 'invoice') {
  return queryOptions({
    queryKey: salesKeys.publicLink(id, entity),
    queryFn: ({ signal }) =>
      api.get<PublicLinkStatus>(path(SHARE_BASE[entity], id, 'public-link'), { signal }),
    staleTime: 0,
  })
}

/** Creates the share link (201 with the URL, shown this once) or reports the active one (no URL). */
export function useCreatePublicLink(id: string, entity: ShareEntity = 'invoice') {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: PublicLinkCreate) =>
      api.post<PublicLinkIssued>(path(SHARE_BASE[entity], id, 'public-link'), input),
    onSuccess: ({ token: _token, url: _url, ...status }) =>
      queryClient.setQueryData(salesKeys.publicLink(id, entity), status),
  })
}

/** Revokes the active link: whoever holds it can no longer open the document. */
export function useRevokePublicLink(id: string, entity: ShareEntity = 'invoice') {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api.delete<void>(path(SHARE_BASE[entity], id, 'public-link')),
    onSettled: () => queryClient.invalidateQueries({ queryKey: salesKeys.publicLink(id, entity) }),
  })
}

/**
 * `GET /public/documents/:token`: what a share link shows, with no session. A 401 here is not an
 * expired session (there is none), so it never sends anyone to the sign-in page.
 */
export function publicDocumentQueryOptions(token: string) {
  return queryOptions({
    queryKey: ['public-document', token] as const,
    queryFn: ({ signal }) =>
      api.get<PublicDocumentView>(`/public/documents/${encodeURIComponent(token)}`, {
        signal,
        skipAuthRedirect: true,
      }),
    retry: false,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  })
}

export function useDeleteInvoice(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api.delete<void>(path('/sales/invoices', id)),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: salesKeys.invoice(id) })
      return invalidateSales(queryClient)
    },
  })
}

// ── quotations ───────────────────────────────────────────────────────────────

function quotationFilters(query: Omit<QuotationListParams, 'page' | 'pageSize'>) {
  return {
    sort: query.sort,
    q: query.q,
    status: query.status,
    expired: query.expired,
    customerId: query.customerId,
    projectId: query.projectId,
    from: query.from,
    to: query.to,
    createdBy: query.createdBy,
  }
}

export function quotationListQueryOptions(query: QuotationListParams) {
  return queryOptions({
    queryKey: salesKeys.quotationList(query),
    queryFn: ({ signal }) =>
      api.get<Page<QuotationListItem>>('/sales/quotations', {
        signal,
        query: { page: query.page, pageSize: query.pageSize, ...quotationFilters(query) },
      }),
    placeholderData: keepPreviousData,
  })
}

/** Counts per view tab (and the open offers' totals) for the list's other filters. */
export function quotationSummaryQueryOptions(query: QuotationSummaryParams) {
  return queryOptions({
    queryKey: salesKeys.quotationSummary(query),
    queryFn: ({ signal }) =>
      api.get<QuotationSummary>('/sales/quotations/summary', { signal, query: { ...query } }),
    placeholderData: keepPreviousData,
  })
}

/** `GET /sales/quotations/export` (sales.export): the list's view, filters and sort as CSV. */
export function exportQuotations(query: QuotationListParams, language: Language): Promise<DownloadedFile> {
  return api.download('/sales/quotations/export', {
    query: { format: 'csv', lang: language, ...quotationFilters(query) },
    fallbackName: 'quotations.csv',
  })
}

export function quotationQueryOptions(id: string) {
  return queryOptions({
    queryKey: salesKeys.quotation(id),
    queryFn: ({ signal }) => api.get<Quotation>(path('/sales/quotations', id), { signal }),
  })
}

export function useSaveQuotation(id: string | null) {
  return useDocumentMutation(
    (input: QuotationInput | QuotationUpdate) =>
      id
        ? api.put<Quotation>(path('/sales/quotations', id), input)
        : api.post<Quotation>('/sales/quotations', input),
    salesKeys.quotation,
  )
}

/**
 * Approving or rejecting sends the version shown, so nobody decides on content edited since (409). Any
 * refusal refetches the quotation: the page then shows what is there now.
 */
function useQuotationDecision<TInput>(
  idOf: (input: TInput) => string,
  request: (input: TInput) => Promise<Quotation>,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: request,
    onSuccess: (doc) => {
      queryClient.setQueryData(salesKeys.quotation(doc.id), doc)
      return invalidateSales(queryClient)
    },
    onError: (_error, input) => queryClient.invalidateQueries({ queryKey: salesKeys.quotation(idOf(input)) }),
  })
}

/** Approves the quotation at the version the approver read. */
export function useApproveQuotation(id: string) {
  return useQuotationDecision(
    () => id,
    (version: number) => api.post<Quotation>(path('/sales/quotations', id, 'approve'), { version }),
  )
}

export function useRejectQuotation(id: string) {
  return useQuotationDecision(
    () => id,
    (input: { reason: string | null; version: number }) =>
      api.post<Quotation>(path('/sales/quotations', id, 'reject'), input),
  )
}

/** "Save and approve": approves the quotation just saved, at the version the save returned. */
export function useApproveSavedQuotation() {
  return useQuotationDecision(
    (input: { id: string; version: number }) => input.id,
    ({ id, version }: { id: string; version: number }) =>
      api.post<Quotation>(path('/sales/quotations', id, 'approve'), { version }),
  )
}

/** "Mark lost" (Q6): an approved offer the customer declined becomes REJECTED, with the reason. */
export function useMarkQuotationLost(id: string) {
  return useDocumentMutation(
    (reason: string) => api.post<Quotation>(path('/sales/quotations', id, 'mark-lost'), { reason }),
    salesKeys.quotation,
  )
}

export function useConvertQuotation(id: string) {
  return useDocumentMutation(
    (input: ConvertQuotationInput) => api.post<Quotation>(path('/sales/quotations', id, 'convert'), input),
    salesKeys.quotation,
  )
}

export function useDeleteQuotation(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api.delete<void>(path('/sales/quotations', id)),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: salesKeys.quotation(id) })
      return invalidateSales(queryClient)
    },
  })
}

// ── receipts ─────────────────────────────────────────────────────────────────

export type ReceiptListItem = Omit<Receipt, 'allocations'>

function receiptFilters(query: Omit<ReceiptListParams, 'page' | 'pageSize'>) {
  return {
    sort: query.sort,
    q: query.q,
    status: query.status,
    hasOpenCredit: query.hasOpenCredit,
    method: query.method,
    treasuryAccountId: query.treasuryAccountId,
    customerId: query.customerId,
    from: query.from,
    to: query.to,
    createdBy: query.createdBy,
  }
}

export function receiptListQueryOptions(query: ReceiptListParams) {
  return queryOptions({
    queryKey: salesKeys.receiptList(query),
    queryFn: ({ signal }) =>
      api.get<Page<ReceiptListItem>>('/sales/receipts', {
        signal,
        query: { page: query.page, pageSize: query.pageSize, ...receiptFilters(query) },
      }),
    placeholderData: keepPreviousData,
  })
}

/** Counts per view tab (and the amount received, the credit left) for the list's other filters. */
export function receiptSummaryQueryOptions(query: ReceiptSummaryParams) {
  return queryOptions({
    queryKey: salesKeys.receiptSummary(query),
    queryFn: ({ signal }) =>
      api.get<ReceiptSummary>('/sales/receipts/summary', { signal, query: { ...query } }),
    placeholderData: keepPreviousData,
  })
}

/** `GET /sales/receipts/export` (sales.export): the list's view, filters and sort as CSV. */
export function exportReceipts(query: ReceiptListParams, language: Language): Promise<DownloadedFile> {
  return api.download('/sales/receipts/export', {
    query: { format: 'csv', lang: language, ...receiptFilters(query) },
    fallbackName: 'receipts.csv',
  })
}

export function receiptQueryOptions(id: string) {
  return queryOptions({
    queryKey: salesKeys.receipt(id),
    queryFn: ({ signal }) => api.get<Receipt>(path('/sales/receipts', id), { signal }),
  })
}

export function useCreateReceipt() {
  return useDocumentMutation(
    (input: ReceiptInput) => api.post<Receipt>('/sales/receipts', input),
    salesKeys.receipt,
    { ledger: true },
  )
}

export function useAllocateReceipt(id: string) {
  return useDocumentMutation(
    (input: AllocateInput) => api.post<Receipt>(path('/sales/receipts', id, 'allocations'), input),
    salesKeys.receipt,
  )
}

export function useUnallocateReceipt(id: string) {
  return useDocumentMutation(
    (allocationId: string) =>
      api.delete<Receipt>(
        `${path('/sales/receipts', id, 'allocations')}/${encodeURIComponent(allocationId)}`,
      ),
    salesKeys.receipt,
  )
}

export function useVoidReceipt(id: string) {
  return useDocumentMutation(
    (reason: string) => api.post<Receipt>(path('/sales/receipts', id, 'void'), { reason }),
    salesKeys.receipt,
    { ledger: true },
  )
}

// ── credit notes ─────────────────────────────────────────────────────────────

function creditNoteFilters(query: Omit<CreditNoteListParams, 'page' | 'pageSize'>) {
  return {
    sort: query.sort,
    q: query.q,
    kind: query.kind,
    status: query.status,
    hasOpenCredit: query.hasOpenCredit,
    customerId: query.customerId,
    invoiceId: query.invoiceId,
    projectId: query.projectId,
    from: query.from,
    to: query.to,
    createdBy: query.createdBy,
  }
}

export function creditNoteListQueryOptions(query: CreditNoteListParams) {
  return queryOptions({
    queryKey: salesKeys.creditNoteList(query),
    queryFn: ({ signal }) =>
      api.get<Page<CreditNoteListItem>>('/sales/credit-notes', {
        signal,
        query: { page: query.page, pageSize: query.pageSize, ...creditNoteFilters(query) },
      }),
    placeholderData: keepPreviousData,
  })
}

/** Counts per view tab (and the credit left) for the list's other filters. */
export function creditNoteSummaryQueryOptions(query: CreditNoteSummaryParams) {
  return queryOptions({
    queryKey: salesKeys.creditNoteSummary(query),
    queryFn: ({ signal }) =>
      api.get<CreditNoteSummary>('/sales/credit-notes/summary', { signal, query: { ...query } }),
    placeholderData: keepPreviousData,
  })
}

/** `GET /sales/credit-notes/export` (sales.export): the list's view, filters and sort as CSV. */
export function exportCreditNotes(query: CreditNoteListParams, language: Language): Promise<DownloadedFile> {
  return api.download('/sales/credit-notes/export', {
    query: { format: 'csv', lang: language, ...creditNoteFilters(query) },
    fallbackName: 'credit-notes.csv',
  })
}

export function creditNoteQueryOptions(id: string) {
  return queryOptions({
    queryKey: salesKeys.creditNote(id),
    queryFn: ({ signal }) => api.get<CreditNote>(path('/sales/credit-notes', id), { signal }),
  })
}

export function useSaveCreditNote(id: string | null) {
  return useDocumentMutation(
    (input: CreditNoteInput | CreditNoteUpdate) =>
      id
        ? api.put<CreditNote>(path('/sales/credit-notes', id), input)
        : api.post<CreditNote>('/sales/credit-notes', input),
    salesKeys.creditNote,
  )
}

export function usePostCreditNote(id: string) {
  return useDocumentMutation(
    (version: number) => api.post<CreditNote>(path('/sales/credit-notes', id, 'post'), { version }),
    salesKeys.creditNote,
    { ledger: true },
  )
}

export function useVoidCreditNote(id: string) {
  return useDocumentMutation(
    (reason: string) => api.post<CreditNote>(path('/sales/credit-notes', id, 'void'), { reason }),
    salesKeys.creditNote,
    { ledger: true },
  )
}

export function useDeleteCreditNote(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api.delete<void>(path('/sales/credit-notes', id)),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: salesKeys.creditNote(id) })
      return invalidateSales(queryClient)
    },
  })
}

export function useAllocateCreditNote(id: string) {
  return useDocumentMutation(
    (input: AllocateInput) => api.post<CreditNote>(path('/sales/credit-notes', id, 'allocations'), input),
    salesKeys.creditNote,
  )
}

export function useUnallocateCreditNote(id: string) {
  return useDocumentMutation(
    (allocationId: string) =>
      api.delete<CreditNote>(
        `${path('/sales/credit-notes', id, 'allocations')}/${encodeURIComponent(allocationId)}`,
      ),
    salesKeys.creditNote,
  )
}

// ── receivables reports and settings ─────────────────────────────────────────

export function customerStatementQueryOptions(customerId: string, from: string, to: string) {
  return queryOptions({
    queryKey: salesKeys.statement(customerId, from, to),
    queryFn: ({ signal }) =>
      api.get<CustomerStatement>(path('/customers', customerId, 'statement'), {
        signal,
        query: { from, to },
      }),
    placeholderData: keepPreviousData,
  })
}

export function agingQueryOptions(asOf: string) {
  return queryOptions({
    queryKey: salesKeys.aging(asOf),
    queryFn: ({ signal }) => api.get<AgingRow[]>('/sales/aging', { signal, query: { asOf } }),
    placeholderData: keepPreviousData,
  })
}

export function useSaveSalesSettings() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: SalesSettingsUpdate) => api.put<SalesSettings>('/sales/settings', input),
    onSuccess: (settings) => queryClient.setQueryData(salesKeys.settings, settings),
  })
}

// ── invoice e-mail (R0b MS8, W4) ───────────────────────────────────────────────

/** How often the invoice's messages are read while one is on its way, and for how long at most. */
export const MESSAGE_POLL_MS = 5_000
export const MESSAGE_POLL_CAP_MS = 2 * 60 * 1000

/** GET /sales/invoices/:id/messages (sales.read): the e-mails sent about the invoice, newest first. */
export function invoiceMessagesQueryOptions(invoiceId: string) {
  return queryOptions({
    queryKey: salesKeys.invoiceMessages(invoiceId),
    queryFn: ({ signal }) =>
      api.get<InvoiceMessage[]>(path('/sales/invoices', invoiceId, 'messages'), { signal }),
    staleTime: 0,
  })
}

/**
 * POST /sales/invoices/:id/email (invoices.send and sales.read) → 202: one message per recipient, and
 * the PDF issued in the chosen language. The same `requestId` sends once, however often it is posted.
 */
export function useSendInvoiceEmail(invoiceId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: SendInvoiceEmail) =>
      api.post<SendInvoiceEmailResult>(path('/sales/invoices', invoiceId, 'email'), input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: salesKeys.invoiceMessages(invoiceId) })
      void queryClient.invalidateQueries({ queryKey: ['documents'] })
    },
  })
}
